import { useState, useEffect, useCallback, useRef } from "react";
import api, { errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UserPlus, Download, ShieldCheck, Trash2, FileText, AlertCircle, Users, Upload, FileDown } from "lucide-react";
import ComuneAutocomplete from "@/components/ComuneAutocomplete";
import StatoAutocomplete from "@/components/StatoAutocomplete";
import { validateAlloggiati } from "@/lib/alloggiatiValidator";

// Top countries by frequency for IT accommodations (ISTAT codes)
const COUNTRIES = [
  { code: "100000100", name_it: "Italia", name_en: "Italy" },
  { code: "100000101", name_it: "Germania", name_en: "Germany" },
  { code: "100000102", name_it: "Francia", name_en: "France" },
  { code: "100000103", name_it: "Regno Unito", name_en: "United Kingdom" },
  { code: "100000106", name_it: "Spagna", name_en: "Spain" },
  { code: "100000110", name_it: "Paesi Bassi", name_en: "Netherlands" },
  { code: "100000113", name_it: "Svizzera", name_en: "Switzerland" },
  { code: "100000114", name_it: "Austria", name_en: "Austria" },
  { code: "100000115", name_it: "Belgio", name_en: "Belgium" },
  { code: "100000118", name_it: "Danimarca", name_en: "Denmark" },
  { code: "100000122", name_it: "Norvegia", name_en: "Norway" },
  { code: "100000123", name_it: "Svezia", name_en: "Sweden" },
  { code: "100000124", name_it: "Polonia", name_en: "Poland" },
  { code: "100000129", name_it: "Portogallo", name_en: "Portugal" },
  { code: "100000138", name_it: "Ucraina", name_en: "Ukraine" },
  { code: "100000140", name_it: "Romania", name_en: "Romania" },
  { code: "100000145", name_it: "Ungheria", name_en: "Hungary" },
  { code: "100000216", name_it: "Cina", name_en: "China" },
  { code: "100000218", name_it: "Giappone", name_en: "Japan" },
  { code: "100000221", name_it: "India", name_en: "India" },
  { code: "100000254", name_it: "Stati Uniti d'America", name_en: "United States" },
  { code: "100000253", name_it: "Canada", name_en: "Canada" },
  { code: "100000305", name_it: "Brasile", name_en: "Brazil" },
  { code: "100000308", name_it: "Argentina", name_en: "Argentina" },
  { code: "100000404", name_it: "Australia", name_en: "Australia" },
];

const PROVINCE_CODES = ["AG","AL","AN","AO","AR","AP","AT","AV","BA","BT","BL","BN","BG","BI","BO","BZ","BS","BR","CA","CL","CB","CI","CE","CT","CZ","CH","CO","CS","CR","KR","CN","EN","FM","FE","FI","FG","FC","FR","GE","GO","GR","IM","IS","AQ","SP","LT","LE","LC","LI","LO","LU","MC","MN","MS","MT","VS","ME","MI","MO","MB","NA","NO","NU","OG","OT","OR","PD","PA","PR","PV","PG","PU","PE","PC","PI","PT","PN","PZ","PO","RG","RA","RC","RE","RI","RN","RM","RO","SA","SS","SV","SI","SR","SO","TA","TE","TR","TO","TP","TN","TV","TS","UD","VA","VE","VB","VC","VR","VV","VI","VT"];

export default function GuestsTab() {
  const { lang } = useLang();
  const IT = lang === "it";
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [docTypes, setDocTypes] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [today] = useState(() => new Date().toISOString().slice(0, 10));
  const [downloadDate, setDownloadDate] = useState(new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/guests");
      setGuests(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/guests/doc-types").then((r) => setDocTypes(r.data)).catch(() => {});
  }, []);

  const download = async () => {
    // Client-side pre-flight: refuse to generate the file if any guest for the
    // target date has invalid Alloggiati Web fields — the Polizia portal would
    // reject the whole batch and we'd rather flag it locally.
    const dayGuests = guests.filter((g) => (g.arrival_date || "").slice(0, 10) === downloadDate);
    if (dayGuests.length === 0) {
      toast.error(IT ? `Nessun ospite registrato per ${downloadDate}` : `No guests registered for ${downloadDate}`);
      return;
    }
    const invalid = dayGuests
      .map((g) => ({ g, check: validateAlloggiati(g, { requireArrival: true }) }))
      .filter((x) => !x.check.ok);
    if (invalid.length > 0) {
      const { g, check } = invalid[0];
      const firstErr = Object.values(check.errors)[0];
      toast.error(IT
        ? `${invalid.length}/${dayGuests.length} schede non valide · ${g.last_name || "?"}: ${firstErr}`
        : `${invalid.length}/${dayGuests.length} invalid records · ${g.last_name || "?"}: ${firstErr}`);
      return;
    }
    try {
      const r = await api.get(`/guests/alloggiati.txt`, {
        params: { date: downloadDate },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/plain;charset=iso-8859-1" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `schedine_${downloadDate.replace(/-/g, "")}.txt`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      const count = r.headers["x-guests-count"] || "?";
      toast.success(IT ? `File scaricato: ${count} scheda/e` : `File downloaded: ${count} record(s)`);
      load(); // refresh submitted flag
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) toast.error(IT ? `Nessun ospite registrato per ${downloadDate}` : `No guests registered for ${downloadDate}`);
      else toast.error(errMsg(e));
    }
  };

  const remove = async (id) => {
    if (!confirm(IT ? "Cancellare questa scheda?" : "Delete this record?")) return;
    try { await api.delete(`/guests/${id}`); toast.success(IT ? "Scheda cancellata" : "Record deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const dayGuests = guests.filter((g) => (g.arrival_date || "").slice(0, 10) === downloadDate);
  const dayInvalid = dayGuests
    .map((g) => validateAlloggiati(g, { requireArrival: true }))
    .filter((c) => !c.ok).length;
  const downloadReady = dayGuests.length > 0 && dayInvalid === 0;

  return (
    <div className="space-y-6" data-testid="guests-tab">
      {/* Header + intro */}
      <div className="border border-primary/20 bg-sand/40 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-1"/>
          <div>
            <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold">
              {IT ? "Alloggiati Web · Polizia di Stato" : "Alloggiati Web · State Police"}
            </div>
            <div className="font-display text-xl mt-1">
              {IT ? "Registro obbligatorio ospiti — entro 24h dal check-in" : "Mandatory guest register — within 24h of check-in"}
            </div>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              {IT
                ? "Compila una scheda per ogni ospite maggiorenne. Il file generato è nel formato ufficiale (168 caratteri, Latin-1) pronto da caricare su alloggiatiweb.poliziadistato.it."
                : "Fill one record per adult guest. The generated file is in the official format (168 characters, Latin-1) ready to upload at alloggiatiweb.poliziadistato.it."}
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-end gap-3 justify-between border border-border rounded-lg bg-card p-4">
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Data arrivo per file" : "Arrival date for file"}</Label>
            <Input type="date" value={downloadDate} onChange={(e) => setDownloadDate(e.target.value)} className="w-44" data-testid="download-date"/>
          </div>
          <Button onClick={download} disabled={!downloadReady} className="bg-primary text-primary-foreground hover:bg-primary/90 lift disabled:opacity-50" data-testid="download-alloggiati-btn">
            <Download className="w-4 h-4 mr-1.5"/>
            {IT ? "Scarica file per Questura" : "Download file for Police"}
          </Button>
          {dayGuests.length > 0 && (
            <div className="text-[10px] leading-tight" data-testid="download-validation-hint">
              {dayInvalid === 0 ? (
                <span className="text-sage">✓ {dayGuests.length} {IT ? "scheda/e valide" : "record(s) valid"}</span>
              ) : (
                <span className="text-destructive">⚠ {dayInvalid}/{dayGuests.length} {IT ? "schede con errori" : "invalid record(s)"}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <CsvBulkUpload IT={IT} onImported={load}/>
          <Button onClick={() => setAddOpen(true)} variant="outline" className="lift" data-testid="add-guest-btn">
            <UserPlus className="w-4 h-4 mr-1.5"/>
            {IT ? "Registra nuovo ospite" : "Register new guest"}
          </Button>
        </div>
      </div>

      {/* Guests list */}
      <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid="guests-list">
        <div className="flex items-center justify-between p-3 border-b border-border bg-muted/30">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5"/>
            {IT ? `Ospiti registrati (${guests.length})` : `Registered guests (${guests.length})`}
          </div>
          {loading && <span className="text-primary text-xs animate-pulse">{IT ? "…" : "…"}</span>}
        </div>
        {guests.length === 0 ? (
          <div className="text-center py-12 text-sm text-muted-foreground">
            <FileText className="w-10 h-10 mx-auto opacity-30 mb-2"/>
            {IT ? "Nessun ospite registrato. Clicca \"Registra nuovo ospite\" per iniziare." : "No registered guests. Click \"Register new guest\" to start."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/20 uppercase tracking-[0.15em] text-[10px] text-muted-foreground">
              <tr>
                <th className="text-left p-3">{IT ? "Ospite" : "Guest"}</th>
                <th className="text-left p-3">{IT ? "Arrivo" : "Arrival"}</th>
                <th className="text-center p-3">{IT ? "Notti" : "Nights"}</th>
                <th className="text-left p-3">{IT ? "Documento" : "Document"}</th>
                <th className="text-left p-3">{IT ? "Cittadinanza" : "Nationality"}</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3"></th>
              </tr>
            </thead>
            <tbody>
              {guests.map((g) => {
                const country = COUNTRIES.find((c) => c.code === g.citizenship_code);
                return (
                  <tr key={g.id} className="border-t border-border" data-testid={`guest-row-${g.id}`}>
                    <td className="p-3">
                      <div className="font-medium">{g.last_name} {g.first_name}</div>
                      <div className="text-[10px] text-muted-foreground">{g.gender} · {g.date_of_birth}</div>
                    </td>
                    <td className="p-3">{g.arrival_date}</td>
                    <td className="p-3 text-center">{g.nights}</td>
                    <td className="p-3">
                      <div>{g.doc_type}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">{g.doc_number}</div>
                    </td>
                    <td className="p-3 text-[11px]">{country ? (IT ? country.name_it : country.name_en) : g.citizenship_code}</td>
                    <td className="p-3">
                      {g.submitted_to_polizia
                        ? <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-sage/15 text-sage font-medium"><ShieldCheck className="w-3 h-3"/>{IT ? "Inviato" : "Sent"}</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium"><AlertCircle className="w-3 h-3"/>{IT ? "Da inviare" : "Pending"}</span>}
                    </td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => remove(g.id)} data-testid={`delete-guest-${g.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-destructive"/>
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <AddGuestDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); load(); }} docTypes={docTypes} defaultArrival={today}/>
    </div>
  );
}

function AddGuestDialog({ open, onClose, onCreated, docTypes, defaultArrival }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const empty = {
    guest_type: "16",
    arrival_date: defaultArrival,
    nights: 1,
    last_name: "", first_name: "", gender: "M", date_of_birth: "",
    comune_of_birth_code: "", province_of_birth: "",
    country_of_birth_code: "100000100", citizenship_code: "100000100",
    doc_type: "IDELE", doc_number: "", doc_place_code: "",
  };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ ...empty, arrival_date: defaultArrival }); }, [open]); // eslint-disable-line

  const isItalianBorn = f.country_of_birth_code === "100000100";
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  // Live validation gates the submit button and colours the offending fields.
  const { ok: isValid, errors: valErr } = validateAlloggiati(f, { requireArrival: true });
  const fieldErr = (k) => valErr[k];

  const submit = async () => {
    const check = validateAlloggiati(f, { requireArrival: true });
    if (!check.ok) {
      const first = Object.values(check.errors)[0];
      toast.error(IT ? `Correggi i campi evidenziati · ${first}` : `Fix highlighted fields · ${first}`);
      return;
    }
    setSaving(true);
    try {
      await api.post("/guests", { ...f, nights: parseInt(f.nights, 10) || 1 });
      toast.success(IT ? "Ospite registrato" : "Guest registered");
      onCreated();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-guest-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{IT ? "Registra ospite (Alloggiati Web)" : "Register guest (Alloggiati Web)"}</DialogTitle>
          <DialogDescription>
            {IT ? "Tutti i campi contrassegnati sono obbligatori secondo la normativa Polizia di Stato." : "All marked fields are required by State Police regulation."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Guest type + arrival */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>{IT ? "Tipo alloggiato *" : "Guest type *"}</Label>
              <Select value={f.guest_type} onValueChange={(v) => set("guest_type", v)}>
                <SelectTrigger data-testid="guest-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="16">16 · {IT ? "Ospite singolo" : "Single guest"}</SelectItem>
                  <SelectItem value="17">17 · {IT ? "Capo famiglia" : "Family head"}</SelectItem>
                  <SelectItem value="18">18 · {IT ? "Capo gruppo" : "Group leader"}</SelectItem>
                  <SelectItem value="19">19 · {IT ? "Familiare" : "Family member"}</SelectItem>
                  <SelectItem value="20">20 · {IT ? "Membro gruppo" : "Group member"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{IT ? "Data arrivo *" : "Arrival date *"}</Label>
              <Input type="date" value={f.arrival_date} onChange={(e) => set("arrival_date", e.target.value)} data-testid="guest-arrival" className={fieldErr("arrival_date") ? "border-destructive" : ""}/>
              {fieldErr("arrival_date") && <p className="text-[10px] text-destructive mt-1">{fieldErr("arrival_date")}</p>}
            </div>
            <div>
              <Label>{IT ? "Notti *" : "Nights *"}</Label>
              <Input type="number" min="1" max="30" value={f.nights} onChange={(e) => set("nights", e.target.value)} data-testid="guest-nights" className={fieldErr("nights") ? "border-destructive" : ""}/>
              {fieldErr("nights") && <p className="text-[10px] text-destructive mt-1">{fieldErr("nights")}</p>}
            </div>
          </div>

          {/* Name */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{IT ? "Cognome *" : "Last name *"}</Label>
              <Input value={f.last_name} onChange={(e) => set("last_name", e.target.value.toUpperCase())} placeholder="ROSSI" data-testid="guest-last-name" className={fieldErr("last_name") ? "border-destructive" : ""}/>
              {fieldErr("last_name") && <p className="text-[10px] text-destructive mt-1">{fieldErr("last_name")}</p>}
            </div>
            <div>
              <Label>{IT ? "Nome *" : "First name *"}</Label>
              <Input value={f.first_name} onChange={(e) => set("first_name", e.target.value.toUpperCase())} placeholder="MARIO" data-testid="guest-first-name" className={fieldErr("first_name") ? "border-destructive" : ""}/>
              {fieldErr("first_name") && <p className="text-[10px] text-destructive mt-1">{fieldErr("first_name")}</p>}
            </div>
          </div>

          {/* Personal */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>{IT ? "Sesso *" : "Gender *"}</Label>
              <Select value={f.gender} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger data-testid="guest-gender"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">M · {IT ? "Maschio" : "Male"}</SelectItem>
                  <SelectItem value="F">F · {IT ? "Femmina" : "Female"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>{IT ? "Data di nascita *" : "Date of birth *"}</Label>
              <Input type="date" value={f.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="guest-dob" className={fieldErr("date_of_birth") ? "border-destructive" : ""}/>
              {fieldErr("date_of_birth") && <p className="text-[10px] text-destructive mt-1">{fieldErr("date_of_birth")}</p>}
            </div>
          </div>

          {/* Birth / citizenship */}
          <div>
            <Label>{IT ? "Stato di nascita *" : "Country of birth *"}</Label>
            <StatoAutocomplete
              value={f.country_of_birth_code}
              onChange={(s) => set("country_of_birth_code", s ? s.code : "")}
              testid="guest-country-birth"
            />
          </div>
          {isItalianBorn && (
            <div className="grid grid-cols-2 gap-3 pl-3 border-l-2 border-primary/30">
              <div>
                <Label>{IT ? "Comune di nascita" : "Municipality of birth"}</Label>
                <ComuneAutocomplete
                  value={f.comune_of_birth_code}
                  onChange={(c) => {
                    set("comune_of_birth_code", c ? c.code : "");
                    if (c && c.prov) set("province_of_birth", c.prov);
                  }}
                  placeholder={IT ? "Cerca es. Milano" : "e.g. Milan"}
                  testid="guest-comune"
                />
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {IT ? "Digita almeno 2 lettere per cercare" : "Type at least 2 letters"}
                </div>
              </div>
              <div>
                <Label>{IT ? "Provincia" : "Province"}</Label>
                <Select value={f.province_of_birth} onValueChange={(v) => set("province_of_birth", v)}>
                  <SelectTrigger data-testid="guest-prov"><SelectValue placeholder="MI"/></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {PROVINCE_CODES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div>
            <Label>{IT ? "Cittadinanza *" : "Citizenship *"}</Label>
            <StatoAutocomplete
              value={f.citizenship_code}
              onChange={(s) => set("citizenship_code", s ? s.code : "")}
              testid="guest-citizenship"
            />
          </div>

          {/* Document */}
          <div className="border-t border-border pt-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">{IT ? "Documento di identità" : "Identity document"}</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>{IT ? "Tipo *" : "Type *"}</Label>
                <Select value={f.doc_type} onValueChange={(v) => set("doc_type", v)}>
                  <SelectTrigger data-testid="guest-doc-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {docTypes.map((d) => <SelectItem key={d.code} value={d.code}>{d.code} · {d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{IT ? "Numero *" : "Number *"}</Label>
                <Input value={f.doc_number} onChange={(e) => set("doc_number", e.target.value.toUpperCase())} placeholder="CA12345AB" className={`font-mono ${fieldErr("doc_number") ? "border-destructive" : ""}`} data-testid="guest-doc-number"/>
                {fieldErr("doc_number") && <p className="text-[10px] text-destructive mt-1">{fieldErr("doc_number")}</p>}
              </div>
              <div>
                <Label>{IT ? "Luogo rilascio *" : "Issued at *"}</Label>
                <ComuneAutocomplete
                  value={f.doc_place_code}
                  onChange={(c) => set("doc_place_code", c ? c.code : "")}
                  placeholder={IT ? "Cerca comune…" : "Search…"}
                  testid="guest-doc-place"
                />
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {IT ? "Comune ITA (autocomplete) o inserisci manualmente codice stato estero" : "IT comune (autocomplete) or foreign country code"}
                </div>
                <Input
                  value={f.doc_place_code}
                  onChange={(e) => set("doc_place_code", e.target.value.toUpperCase())}
                  placeholder={IT ? "o inserisci codice manualmente" : "or enter code manually"}
                  maxLength={9}
                  className="font-mono text-[11px] h-7 mt-1"
                  data-testid="guest-doc-place-manual"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end items-center gap-2 pt-2 sticky bottom-0 bg-background pb-1" data-testid="add-guest-footer">
          {!isValid && (
            <span className="text-[10px] text-amber-700 mr-auto flex items-center gap-1" data-testid="add-guest-validation-hint">
              <AlertCircle className="w-3 h-3"/> {IT ? `${Object.keys(valErr).length} campi da completare` : `${Object.keys(valErr).length} fields to complete`}
            </span>
          )}
          <Button variant="outline" onClick={onClose}>{IT ? "Annulla" : "Cancel"}</Button>
          <Button onClick={submit} disabled={saving || !isValid} className="bg-primary text-primary-foreground hover:bg-primary/90 lift disabled:opacity-50" data-testid="save-guest-btn">
            {saving ? "…" : isValid ? (IT ? "Registra ospite" : "Register guest") : (IT ? "Completa i campi" : "Complete fields")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CsvBulkUpload({ IT, onImported }) {
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);
  const fileRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/guests/csv-template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "template_ospiti.csv";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/guests/bulk-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(r.data);
      if (r.data.errors.length === 0) {
        toast.success(IT ? `${r.data.inserted} ospiti importati` : `${r.data.inserted} guests imported`);
      } else {
        toast.warning(IT ? `${r.data.inserted} importati · ${r.data.errors.length} errori` : `${r.data.inserted} imported · ${r.data.errors.length} errors`);
      }
      onImported();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="csv-bulk">
      <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={upload} className="hidden" data-testid="csv-input"/>
      <Button
        variant="ghost" size="sm" onClick={downloadTemplate}
        className="text-xs" data-testid="csv-template-btn"
        title={IT ? "Scarica template CSV" : "Download CSV template"}
      >
        <FileDown className="w-3.5 h-3.5 mr-1"/>{IT ? "Template" : "Template"}
      </Button>
      <Button
        variant="outline" size="sm" onClick={() => fileRef.current?.click()}
        disabled={uploading} className="lift" data-testid="csv-upload-btn"
      >
        <Upload className="w-3.5 h-3.5 mr-1"/>
        {uploading ? (IT ? "Import…" : "Uploading…") : (IT ? "Import CSV" : "Import CSV")}
      </Button>
      {report && report.errors.length > 0 && (
        <Dialog open={!!report} onOpenChange={() => setReport(null)}>
          <DialogContent className="max-w-lg" data-testid="csv-report-dialog">
            <DialogHeader>
              <DialogTitle>{IT ? "Riepilogo import CSV" : "CSV import summary"}</DialogTitle>
              <DialogDescription>
                {IT ? `${report.inserted} righe importate · ${report.errors.length} errori` : `${report.inserted} rows imported · ${report.errors.length} errors`}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-64 overflow-y-auto text-xs space-y-1">
              {report.errors.map((e, i) => (
                <div key={`${e.row}-${i}`} className="border border-destructive/30 bg-destructive/5 rounded p-2">
                  <span className="font-semibold">{IT ? "Riga" : "Row"} {e.row}:</span> {e.error}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
